// <cart-perks> refreshes the multi-item discount, installment and free-shipping rows from the live cart (/cart.js).
// The server renders the initial state from the Liquid cart; this keeps it right after bfcache restores and cart changes.
const money = (cents) =>
  new Intl.NumberFormat(document.documentElement.lang || 'tr-TR', { style: 'currency', currency: window.Shopify?.currency?.active || 'TRY', minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 }).format(cents / 100);

class CartPerks extends HTMLElement {
  #ac;
  #onShow = (event) => { if (event.persisted) this.#refresh(); };
  #onCart = () => this.#refresh();
  // The theme's cart drawer doesn't broadcast cart changes, so also re-read the cart when the drawer
  // opens after an add, when any dialog (the drawer) closes, and when the tab becomes visible again.
  #onClose = (event) => { if (event.target instanceof HTMLDialogElement) this.#refresh(); };
  #onVisible = () => { if (document.visibilityState === 'visible') this.#refresh(); };

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    window.addEventListener('pageshow', this.#onShow, opts);
    document.addEventListener('cart:updated', this.#onCart, opts);
    document.addEventListener('s-cart:opened', this.#onCart, opts);
    document.addEventListener('close', this.#onClose, { ...opts, capture: true });
    document.addEventListener('visibilitychange', this.#onVisible, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #set(name, text, ratio) {
    const status = this.querySelector(`[data-perk-status="${name}"]`);
    const bar = this.querySelector(`[data-perk-progress="${name}"]`);
    if (status) status.textContent = text;
    if (bar) bar.style.width = `${Math.min(100, Math.max(0, ratio * 100))}%`;
  }

  async #refresh() {
    try {
      const response = await fetch(this.dataset.cartJs, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) return;
      const cart = await response.json();
      const d = this.dataset;
      const quantity = Number(d.discountQuantity) || 0;
      const limit = Number(d.installmentCents) || 0;
      const count = cart.item_count;
      const total = cart.total_price;
      this.#set('discount', count >= quantity ? d.done : d.itemsLeft.replace('__N__', quantity - count), quantity ? count / quantity : 1);
      this.#set('installments', total >= limit ? d.done : d.amountLeft.replace('__N__', money(limit - total)), limit ? total / limit : 1);

      // Free shipping: a qualifying product stays "free"; otherwise follow the cart total.
      const shipLimit = Number(d.shippingCents) || 0;
      if (shipLimit && d.shippingProduct !== 'true') {
        const reached = total >= shipLimit;
        const label = this.querySelector('[data-perk-label="shipping"]');
        if (label) label.textContent = reached ? d.shippingCart : d.shippingThreshold;
        this.#set('shipping', reached ? '' : d.amountLeft.replace('__N__', money(shipLimit - total)), total / shipLimit);
      }
    } catch (_) {}
  }
}

if (!customElements.get('cart-perks')) customElements.define('cart-perks', CartPerks);
